import { ArrowRight, Award, BadgeCheck, BookOpenCheck, BriefcaseBusiness, CheckCircle2, GraduationCap, Lightbulb, Rocket, Search, Trophy } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { createStudentGrowthPlan, recordStudentGrowthEvent } from '../api.js'
import StudentBackButton from '../components/StudentBackButton.jsx'
import {
  MAJOR_CAREER_MAJOR_OPTIONS,
  getMajorCareerRecommendation,
} from '../student-major-careers.js'
import { findTermExplanations } from '../student-friendly-explanations.js'

function joinList(items) {
  return Array.isArray(items) ? items.filter(Boolean).join(' / ') : ''
}

function buildRoadmap(direction) {
  return {
    'Stage 1: understand the direction': [
      `Understand what ${direction.name} does every day`,
      `List five core abilities for ${direction.name}`,
      'Collect three senior examples or job descriptions as references',
    ],
    'Stage 2: build foundations': direction.abilities,
    'Stage 3: create visible output': direction.projects,
    'Stage 4: package with competitions, certificates, and resume': [
      ...direction.competitions.slice(0, 2),
      ...direction.certificates.slice(0, 2),
      `Prepare ${direction.name} resume bullet points`,
    ],
  }
}

function DirectionCard({ direction, savingName, onSave }) {
  const terms = findTermExplanations([direction.name, direction.abilities, direction.projects], 3)
  return (
    <article className="major-career-card">
      <header>
        <span><BriefcaseBusiness size={16} /> 常见方向</span>
        <h2>{direction.name}</h2>
        <p>{direction.fit}</p>
      </header>
      <div className="major-career-grid">
        <section>
          <strong><BookOpenCheck size={16} /> 核心能力</strong>
          <ul>{direction.abilities.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
        <section>
          <strong><Rocket size={16} /> 推荐项目/作品</strong>
          <ul>{direction.projects.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
        <section>
          <strong><BadgeCheck size={16} /> 适合的证书</strong>
          <ul>{direction.certificates.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
        <section>
          <strong><Trophy size={16} /> 适合的竞赛</strong>
          <ul>{direction.competitions.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
      </div>
      <div className="major-career-jobs">
        <strong>就业岗位</strong>
        <p>{joinList(direction.jobs)}</p>
      </div>
      <section className="student-friendly-explainer major-career-friendly">
        <div className="student-friendly-tags">
          <span>适合0基础</span>
          <span>推荐学习</span>
          <span>可写简历</span>
        </div>
        <dl>
          <div>
            <dt>这个方向到底做什么？</dt>
            <dd>{direction.fit} 先看它每天解决什么问题、需要什么作品。</dd>
          </div>
          <div>
            <dt>0基础怎么开始？</dt>
            <dd>先选一个最小作品：{direction.projects[0]}。做完能截图、能讲清过程，再补工具和证书。</dd>
          </div>
          <div>
            <dt>能力栈怎么理解？</dt>
            <dd>{direction.abilities.slice(0, 3).join(' / ')} 是优先补的能力，不需要一次学完所有工具。</dd>
          </div>
        </dl>
        {terms.length ? (
          <div className="student-term-mini-list">
            {terms.map((item) => (
              <p key={item.term}><strong>{item.term}</strong>{item.explanation}</p>
            ))}
          </div>
        ) : null}
      </section>
      <div className="major-career-actions">
        <button className="primary-button" type="button" disabled={savingName === direction.name} onClick={() => onSave(direction)}>
          <CheckCircle2 size={16} />
          {savingName === direction.name ? '正在生成...' : '一键生成成长计划'}
        </button>
        <Link className="secondary-button" to={`/explore?direction=${encodeURIComponent(direction.name)}`}>
          去探索方向详情 <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  )
}

function StudentMajorCareerPage() {
  const navigate = useNavigate()
  const [major, setMajor] = useState('软件工程')
  const [customMajor, setCustomMajor] = useState('')
  const [savingName, setSavingName] = useState('')
  const [notice, setNotice] = useState('')
  const selectedMajor = customMajor.trim() || major
  const recommendation = useMemo(() => getMajorCareerRecommendation(selectedMajor), [selectedMajor])

  async function saveDirection(direction) {
    setSavingName(direction.name)
    setNotice('')
    const payload = {
      targetDirection: `${recommendation.major} - ${direction.name}`,
      stage: '入门',
      roadmap: buildRoadmap(direction),
      skills: direction.abilities,
      materials: {
        projects: direction.projects,
        certificates: direction.certificates,
        competitions: direction.competitions,
        jobs: direction.jobs,
        todayAdvice: [
          `今天先了解 ${direction.name} 3 个真实招聘要求`,
          `选一个作品方向：${direction.projects[0]}`,
          `记录一个准备动作：${direction.competitions[0]} ${direction.certificates[0]}`,
        ],
      },
    }
    try {
      await createStudentGrowthPlan(payload)
      setNotice('已生成成长计划，正在进入我的成长')
      navigate('/growth')
    } catch {
      try {
        await recordStudentGrowthEvent({
          eventType: 'major_direction_selected',
          title: `选择专业方向：${recommendation.major} - ${direction.name}`,
          description: '用户从专业推荐页选择了职业方向。',
          metadata: { major: recommendation.major, direction: direction.name },
        })
      } catch {
        // Login is optional here; the CTA should still explain the next step.
      }
      setNotice('已为你整理方向。登录后可以保存为成长计划，也可以先去探索方向页继续细化。')
    } finally {
      setSavingName('')
    }
  }

  return (
    <main className="page-container major-career-page">
      <StudentBackButton />
      <section className="major-career-hero">
        <span><GraduationCap size={16} /> 专业到职业方向</span>
        <h1>不知道本专业能做什么？先从几个看得懂的方向开始</h1>
        <p>输入或选择专业，系统用规则模板给出发展方向、项目作品、证书、竞赛和岗位。</p>
      </section>

      <section className="major-career-picker">
        <label>
          <span>选择常见专业</span>
          <select value={major} onChange={(event) => setMajor(event.target.value)}>
            {MAJOR_CAREER_MAJOR_OPTIONS.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <label>
          <span>或输入你的专业</span>
          <div>
            <Search size={17} />
            <input value={customMajor} onChange={(event) => setCustomMajor(event.target.value)} placeholder="例如：信息管理、智能制造、市场营销" />
          </div>
        </label>
      </section>

      <section className="major-career-summary">
        <Lightbulb size={18} />
        <div>
          <strong>{recommendation.major} 推荐思路</strong>
          <p>{recommendation.summary}</p>
        </div>
        <Link className="secondary-button" to="/more/competitions"><Award size={16} /> Competitions</Link>
        <Link className="secondary-button" to="/more/certificates"><BadgeCheck size={16} /> Certificates</Link>
      </section>

      {notice ? <p className="student-growth-error is-success">{notice}</p> : null}

      <section className="major-career-results">
        {recommendation.directions.map((direction) => (
          <DirectionCard
            direction={direction}
            key={direction.name}
            onSave={saveDirection}
            savingName={savingName}
          />
        ))}
      </section>
    </main>
  )
}

export default StudentMajorCareerPage
